import { useEffect } from 'react';
import '@n8n/chat/style.css';
import '../styles/chat-theme.css'; // must come after the package stylesheet
import { createChat } from '@n8n/chat';

/**
 * n8n chat webhook. This must be the *Chat Trigger* webhook URL, e.g.
 *   https://n8n.example.com/webhook/<workflow-webhook-id>/chat
 * The bare instance URL will not work.
 */
const FALLBACK_CHAT_URL = 'http://72.62.193.111:32768/webhook/hf-website-chat/chat';

const CHAT_URL =
  (import.meta.env?.VITE_CHAT_URL || '').trim().replace(/\/+$/, '') || FALLBACK_CHAT_URL;

/**
 * Replaying the transcript costs one extra webhook POST per page load. n8n only
 * answers it when the Chat Trigger has "Load Previous Session" set to "From Memory";
 * otherwise the request can run the whole workflow (and burn AI tokens) for nothing.
 * So this stays opt-in: set VITE_CHAT_LOAD_HISTORY=true once that option is on.
 */
const LOAD_HISTORY = import.meta.env?.VITE_CHAT_LOAD_HISTORY === 'true';

const SESSION_STORAGE_KEY = 'n8n-chat/sessionId';

function getSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `hf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // Private mode / storage blocked: fall back to a per-load session.
    return `hf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

type HistoryMessage = { id: string; text: string; sender: 'user' | 'bot' };

/**
 * Fetch the stored transcript ourselves instead of using `loadPreviousSession: true`.
 *
 * The widget awaits that request during mount and never creates a session if it
 * rejects — which leaves the window rendered with no text input at all. Doing the
 * fetch here means a failing/unreachable webhook only costs us the history.
 */
async function fetchPreviousMessages(
  webhookUrl: string,
  sessionId: string
): Promise<HistoryMessage[]> {
  if (!LOAD_HISTORY) return [];
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'loadPreviousSession', sessionId }),
    });
    if (!response.ok) return [];

    const payload = await response.json();
    const rows: any[] = Array.isArray(payload?.data) ? payload.data : [];

    return rows
      .map((row, index) => ({
        id: `history-${index}`,
        text: row?.kwargs?.content ?? '',
        sender: (Array.isArray(row?.id) ? row.id.join('.') : String(row?.id ?? '')).includes(
          'HumanMessage'
        )
          ? ('user' as const)
          : ('bot' as const),
      }))
      .filter((message) => typeof message.text === 'string' && message.text.length > 0);
  } catch {
    return [];
  }
}

/** Module-scope singleton: the widget mounts itself onto <body> and lives for
 *  the whole app session, so it must be built exactly once — and must NOT be
 *  torn down by StrictMode's throwaway first mount. */
let chatInitialized = false;

async function initChat() {
  // The widget still renders with a bad URL (so the UI is testable); only
  // sending/receiving messages needs a real Chat Trigger webhook.
  if (!/\/(webhook|webhook-test)\//.test(CHAT_URL)) {
    console.warn(
      `[ChatWidget] VITE_CHAT_URL ("${CHAT_URL}") is not an n8n Chat Trigger webhook URL. ` +
        'Messages will fail until it is set to https://<host>/webhook/<id>/chat'
    );
  }

  const sessionId = getSessionId();
  const messageHistory = await fetchPreviousMessages(CHAT_URL, sessionId);

  createChat({
    webhookUrl: CHAT_URL,
    webhookConfig: { method: 'POST' },
    mode: 'window',
    showWelcomeScreen: false,
    showWindowCloseButton: true,
    // Session is managed above so the input always renders, even if n8n is down.
    loadPreviousSession: false,
    sessionId,
    messageHistory,
    initialMessages: messageHistory.length
      ? []
      : [
          'Assalam o Alaikum! 👋',
          'Hammad Foundation mein khush aamdeed. Main aapki kya madad kar sakta hoon?',
        ],
    i18n: {
      en: {
        title: 'Hammad Foundation',
        subtitle: 'Hum aapki madad ke liye yahan hain',
        inputPlaceholder: 'Apna sawal likhein...',
        getStarted: 'Baat shuru karein',
        footer: '',
        closeButtonTooltip: 'Band karein',
      },
    },
  });
}

export default function ChatWidget() {
  useEffect(() => {
    if (chatInitialized) return;
    chatInitialized = true;

    initChat().catch((error) => {
      chatInitialized = false;
      console.error('[ChatWidget] failed to initialise', error);
    });
    // No cleanup on purpose: see chatInitialized above.
  }, []);

  return null;
}
